import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { calculatePower } from '../../../electrical/electricalFunctions';

/**
 * Simple SupplyPoint için electrical calculation service
 * Karmaşık memoization ve chain hesapları yerine basit direct calculation
 * Redux state objesi ile çalışır
 */
export class SimpleElectricalService {

  /*   * Helper: Redux state'den component bulur   */
  static getComponent(simpleSupply, componentId) {
    const components = simpleSupply.components || [];
    return components.find(c => c.id === componentId) || null;
  }

  /**   * Helper: Redux state'den feeder bulur   */
  static getFeeder(simpleSupply, feederId) {
    const feeders = simpleSupply.feeders || [];
    return feeders.find(f => f.id === feederId) || null;
  }

  /**   * Helper: Belirli kind'daki component'leri bulur   */
  static getComponentByKind(simpleSupply, kind) {
    const components = simpleSupply.components || [];
    return components.filter(c => c.kind === kind);
  }

  /**   * Helper: Component'in outgoing feeder'larını bulur   */
  static getOutgoingFeeders(simpleSupply, componentId) {
    const feeders = simpleSupply.feeders || [];
    return feeders.filter(f => f.from === componentId);
  }

  /**  * Helper: Component'in upstream component'lerini bulur   */
  static getUpstreamComponents(simpleSupply, startComponentId) {
    const upstreamComponents = [];
    const visited = new Set();

    const findUpstream = (componentId) => {
      if (visited.has(componentId)) return;
      visited.add(componentId);

      // Bu component'e gelen feeder'ları bul
      const feeders = simpleSupply.feeders || [];
      const incomingFeeders = feeders.filter(f => f.to === componentId);

      incomingFeeders.forEach(feeder => {
        const upstreamComponent = this.getComponent(simpleSupply, feeder.from);
        if (upstreamComponent && !upstreamComponents.find(c => c.id === upstreamComponent.id)) {
          upstreamComponents.push(upstreamComponent);
          // Recursively find upstream components
          findUpstream(upstreamComponent.id);
        }
      });
    };

    findUpstream(startComponentId);
    return upstreamComponents;
  }

  /**
   * İnkremental güç propagasyonu - Parent chain'e güç ekleme/çıkarma
   * 🆕 SIMPLIFIED: parentComponentId field'ını kullanır
   */
  static incrementalPowerPropagation(startComponentId, powerDelta, simpleSupply) {

    const updatedComponents = [];
    const visited = new Set();

    const propagateToParent = (componentId, power) => {
      if (visited.has(componentId)) {
        return;
      }
      visited.add(componentId);

      const component = this.getComponent(simpleSupply, componentId);
      if (!component) {
        return;
      }

      // 🆕 BASIT: parentComponentId field'ını kullan
      const parentId = component.parentComponentId;


      if (!parentId) {
        return;
      }

      const parentComponent = this.getComponent(simpleSupply, parentId);
      if (parentComponent) {
        // Parent'ın installedPower, demandPower, current, socketPower'ını güncelle
        const currentPower = parentComponent.electrical?.installedPower || 0;
        const newPower = Math.max(0, currentPower + power); // Negative olmayacak şekilde
        const phaseCount = parentComponent.electrical?.phaseCount || 1;
        const voltage = 220;
        const newCurrent = newPower / (voltage * phaseCount);

        updatedComponents.push({
          componentId: parentComponent.id,
          electrical: {
            ...parentComponent.electrical,
            installedPower: newPower,
            demandPower: newPower,
            current: newCurrent,
            socketPower: newPower // socketPower = installedPower
          }
        });

        // Recursively parent'ın parent'ına propagate et
        propagateToParent(parentComponent.id, power);
      } else {
        
      }
    };

    // Start component'in parent'larına propagate et
    propagateToParent(startComponentId, powerDelta);

    return updatedComponents;
  }

  /**   * Tek bir component'in electrical değerlerini hesaplar   */
  static calculateComponentElectrical(componentId, simpleSupply) {
    // ✅ SIMPLE SUPPLY REDUX STATE: Use helper method
    const component = this.getComponent(simpleSupply, componentId);
    if (!component) return null;

    // Component tipine göre güç hesaplama
    switch (component.kind) {
      case COMPONENT_KINDS.SUPPLY:
        // Supply için downstream'den topla
        return this.calculateSupplyPower(componentId, simpleSupply);

      case COMPONENT_KINDS.METER_PANEL:
        // Meter panel için downstream'den topla
        return this.calculateMainPanelPower(componentId, simpleSupply);

      case COMPONENT_KINDS.DIST_PANEL:
        // Panel'ler için outgoing feeder'ların toplamı
        return this.calculatePanelPower(componentId, simpleSupply);
      case COMPONENT_KINDS.PANEL:
        // Panel'ler için outgoing feeder'ların toplamı
        return this.calculatePanelPower(componentId, simpleSupply);

      case COMPONENT_KINDS.BRANCH:
        // Branch için direkt power
        return {
          installedPower: component.electrical.installedPower || 0,
          lightPower: component.metadata.branchType === 'lighting' ? component.electrical.installedPower : 0,
          socketPower: component.metadata.branchType !== 'lighting' ? component.electrical.installedPower : 0,
          demandPower: component.electrical.demandPower || component.electrical.installedPower || 0,
          current: component.electrical.current || 0
        };

      case COMPONENT_KINDS.LOAD:
        // Load için direkt power
        return {
          installedPower: component.electrical.installedPower || 0,
          lightPower: component.metadata.loadType === 'lighting' ? component.electrical.installedPower : 0,
          socketPower: component.metadata.loadType !== 'lighting' ? component.electrical.installedPower : 0,
          demandPower: component.electrical.demandPower || component.electrical.installedPower || 0,
          current: component.electrical.current || 0
        };

      default:
        return null;
    }
  }

  /**
   * Panel component'i için elektriksel hesaplama
   * 🆕 SIMPLIFIED: Sadece elektriksel hesaplamalar (demandPower, current)
   * installedPower incremental system tarafından güncellenir
   */
  static calculatePanelPower(panelId, simpleSupply) {
    const panel = this.getComponent(simpleSupply, panelId);
    if (!panel) return null;


    // 🆕 Panel'in mevcut installedPower'ını kullan (incremental system tarafından güncellenmiş)
    const installedPower = panel.electrical?.installedPower || 0;

    // Light/Socket power'ı downstream component'lerden hesapla (kaldırabilir miyiz?)
    let lightPower = 0;
    let socketPower = 0;

    const outgoingFeeders = this.getOutgoingFeeders(simpleSupply, panelId);

    // Light/Socket breakdown için downstream'i tara (sadece kategorilendirme için)
    outgoingFeeders.forEach(feeder => {
      const targetComponent = this.getComponent(simpleSupply, feeder.to);
      if (targetComponent && (targetComponent.kind === COMPONENT_KINDS.BRANCH || targetComponent.kind === COMPONENT_KINDS.LOAD)) {
        const componentPower = targetComponent.electrical?.installedPower || 0;

        // Sadece light/socket kategorilendirmesi yap
        if (targetComponent.metadata?.branchType === 'lighting' || targetComponent.metadata?.loadType === 'lighting') {
          lightPower += componentPower;
        } else {
          socketPower += componentPower;
        }
      }
    });

    // calculatePower ile demand power ve current hesapla
    const electricalResult = calculatePower({
      installedPower,
      lightPower,
      socketPower,
      demandFactor: panel.electrical?.demandFactor || "60-40%",
      phaseCount: panel.electrical?.phaseCount || 3,
      purposeOfUse: panel.electrical?.purposeOfUse || "Mesken",
      subscribeType: panel.electrical?.subscribeType || "Mesken",
      type: panel.metadata?.type || panel.kind,
      feederType: panel.metadata?.feederType
    });
    const result = {
      installedPower,
      lightPower,
      socketPower,
      ...electricalResult
    };


    return result;
  }

  /**
   * MainPanel (METER_PANEL) için elektriksel hesaplama
   * 🆕 SIMPLIFIED: Mevcut installedPower kullanılır, sadece elektriksel hesaplamalar yapılır
   */
  static calculateMainPanelPower(mainPanelId, simpleSupply) {
    const mainPanel = this.getComponent(simpleSupply, mainPanelId);
    if (!mainPanel) return null;


    // 🆕 MainPanel'in mevcut installedPower'ını kullan (incremental system tarafından güncellenmiş)
    const installedPower = mainPanel.electrical?.installedPower || 0;

    // calculatePower fonksiyonu ile detaylı electrical hesaplamalar
    const powerData = {
      installedPower,
      lightPower: 0,
      socketPower: installedPower,
      purposeOfUse: mainPanel.metadata?.purposeOfUse || "Mesken",
      type: "meterPanel",
      phaseCount: installedPower > 10000 ? 3 : 1
    };

    const calculatedElectrical = calculatePower(powerData);

    const result = {
      installedPower,
      demandPower: calculatedElectrical.demandPower,
      lightPower: 0,
      socketPower: installedPower,
      current: calculatedElectrical.current,
      phaseCount: calculatedElectrical.phaseCount,
      demandFactor: calculatedElectrical.demandFactor,
      purposeOfUse: calculatedElectrical.purposeOfUse
    };

    return result;
  }

  /**
   * Supply component için elektriksel hesaplama
   * 🆕 SIMPLIFIED: Mevcut installedPower kullanılır, sadece elektriksel hesaplamalar yapılır
   */
  static calculateSupplyPower(supplyId, simpleSupply) {
    const supplyComponent = this.getComponent(simpleSupply, supplyId);
    if (!supplyComponent) return null;


    // 🆕 Supply'ın mevcut installedPower'ını kullan (incremental system tarafından güncellenmiş)
    const totalInstalledPower = supplyComponent.electrical?.installedPower || 0;

    // Light/Socket breakdown için sistem taraması (gerekirse optimize edilebilir)
    let totalLightPower = 0;
    let totalSocketPower = 0;

    // Sadece light/socket kategorilendirmesi için downstream'i tara
    simpleSupply.components.forEach(component => {
      if (component.id !== supplyId && component.kind !== COMPONENT_KINDS.SUPPLY) {
        if (component.kind === COMPONENT_KINDS.BRANCH || component.kind === COMPONENT_KINDS.LOAD) {
          const componentPower = component.electrical?.installedPower || 0;
          if (component.metadata?.branchType === 'lighting' || component.metadata?.loadType === 'lighting') {
            totalLightPower += componentPower;
          } else {
            totalSocketPower += componentPower;
          }
        }
      }
    });

    const electricalResult = calculatePower({
      installedPower: totalInstalledPower,
      lightPower: totalLightPower,
      socketPower: totalSocketPower,
      demandFactor: supplyComponent.electrical?.demandFactor || "60-40%",
      phaseCount: supplyComponent.electrical?.phaseCount || 3,
      purposeOfUse: "Mesken",
      subscribeType: "Mesken",
      type: "supply",
      feederType: "supply"
    });

    return {
      installedPower: totalInstalledPower,
      lightPower: totalLightPower,
      socketPower: totalSocketPower,
      ...electricalResult
    };
  }

  /**
   * Tüm component'lerin electrical değerlerini yeniden hesaplar
   * @param {SimpleSupplyPoint} simpleSupply - Simple supply state
   * @returns {Array} Component ID ve electrical değerler
   */
  static recalculateAllComponents(simpleSupply) {

    const results = [];

    // Bottom-up hesaplama: önce branch/load'lar, sonra panel'lar, son olarak supply
    const calculationOrder = [
      ...this.getComponentByKind(simpleSupply, COMPONENT_KINDS.LOAD),
      ...this.getComponentByKind(simpleSupply, COMPONENT_KINDS.BRANCH),
      ...this.getComponentByKind(simpleSupply, COMPONENT_KINDS.DIST_PANEL),
      ...this.getComponentByKind(simpleSupply, COMPONENT_KINDS.METER_PANEL),
      ...this.getComponentByKind(simpleSupply, COMPONENT_KINDS.SUPPLY)
    ];

    calculationOrder.forEach(component => {
      const electrical = this.calculateComponentElectrical(component.id, simpleSupply);
      if (electrical) {
        results.push({
          componentId: component.id,
          electrical
        });

      }
    });

    return results;
  }

  /**
   * Feeder'ın electrical değerlerini hesaplar
   * @param {string} feederId - Feeder ID
   * @param {SimpleSupplyPoint} simpleSupply - Simple supply state
   * @returns {object} Feeder electrical değerler
   */
  static calculateFeederElectrical(feederId, simpleSupply) {
    const feeder = this.getFeeder(simpleSupply, feederId);
    if (!feeder) return null;

    const targetComponent = this.getComponent(simpleSupply, feeder.to);
    if (!targetComponent) return null;

    // Target component'in electrical değerlerini al
    const targetElectrical = this.calculateComponentElectrical(feeder.to, simpleSupply);
    if (!targetElectrical) return null;

    // Feeder'ın power değerlerini target component'ten al
    return {
      installedPower: targetElectrical.installedPower || 0,
      demandPower: targetElectrical.demandPower || 0,
      current: targetElectrical.current || 0
    };
  }

  /**
   * Component'in upstream chain'ini hesaplar (supply box'a kadar)
   * FIXED: Doğru sırayla bottom-up hesaplama */
  static calculateUpstreamChain(startComponentId, simpleSupply) {

    // Tam zinciri oluştur: starting component'ten supply'a kadar
    const chainToSupply = this.buildUpstreamChain(simpleSupply, startComponentId);
    const results = [];

    // 🆕 DOĞRU SIRA: Child'dan parent'a doğru hesapla
    // Önce load'lar, sonra panel'lar, en son supply
    chainToSupply.forEach((component, index) => {

      const electrical = this.calculateComponentElectrical(component.id, simpleSupply);
      if (electrical) {
        results.push({
          componentId: component.id,
          electrical
        });
      }
    });

    return results;
  }

  /**
   * Başlangıç componentinden supply'a kadar zinciri oluşturur
   * @param {object} simpleSupply - Simple supply state
   * @param {string} startComponentId - Başlangıç component ID
   * @returns {Array} Component'ler (child'dan parent'a sıralı)
   */
  /**
   * 🆕 SIMPLIFIED: parentComponentId kullanarak upstream chain oluştur
   */
  static buildUpstreamChain(simpleSupply, startComponentId) {
    const chain = [];
    const visited = new Set();

    // 🆕 BASİT LOOP: parentComponentId takip et
    let currentComponentId = startComponentId;
    while (currentComponentId) {
      if (visited.has(currentComponentId)) {
         break;
      }
      visited.add(currentComponentId);

      const component = this.getComponent(simpleSupply, currentComponentId);
      if (!component) {
         break;
      }

      chain.push(component);
 
      // Supply'a ulaştık mı?
      if (component.kind === COMPONENT_KINDS.SUPPLY) { 
        break;
      }

      // Parent'a geç
      currentComponentId = component.parentComponentId;
     
    } 

    return chain;
  }

  /**
   * Başlangıç componentinden supply'a kadar olan tam zinciri hesaplar
   * @param {string} startComponentId - Başlangıç component ID
   * @param {SimpleSupplyPoint} simpleSupply - Simple supply state
   * @returns {Array} Supply'a kadar olan tüm component'ler
   */
  static calculateFullChainToSupply(startComponentId, simpleSupply) {
    const chain = [];
    const visited = new Set();

    const buildChain = (componentId) => {
      if (visited.has(componentId)) return;
      visited.add(componentId);

      const component = this.getComponent(simpleSupply, componentId);
      if (!component) return;

      chain.push(component);

      // Bu component'e gelen feeder'ları bul ve upstream'e devam et
      const feeders = simpleSupply.feeders || [];
      const incomingFeeders = feeders.filter(f => f.to === componentId);

      incomingFeeders.forEach(feeder => {
        buildChain(feeder.from);
      });
    };

    buildChain(startComponentId);

    // Supply component'i en sonda olacak şekilde sırala
    const supplyComponent = chain.find(c => c.kind === COMPONENT_KINDS.SUPPLY);
    const otherComponents = chain.filter(c => c.kind !== COMPONENT_KINDS.SUPPLY);

    return [...otherComponents, supplyComponent].filter(Boolean);
  }
}